import { useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import PromotionalCodeService from '@/common/services/PromotionalCodeService'

import { DEBOUNCE_DELAYS } from '../constants/formConstants'

/**
 * Custom hook for validating promotional/voucher codes with debouncing
 *
 * @param {string} voucherCode - The voucher code to validate
 * @returns {{
 *   isValid: boolean,
 *   isValidating: boolean,
 *   debouncedCode: string
 * }}
 *
 * @example
 * const { isValid, isValidating, debouncedCode } = useVoucherValidation(watch('voucherCode'))
 *
 * <TextField
 *   error={debouncedCode && !isValid}
 *   helperText={debouncedCode && !isValid ? 'Invalid code' : ''}
 * />
 */
export const useVoucherValidation = (voucherCode) => {
    const [isValid, setIsValid] = useState(true)
    const [isValidating, setIsValidating] = useState(false)
    const [debouncedCode] = useDebounce(voucherCode, DEBOUNCE_DELAYS.VOUCHER_VALIDATION)

    const validateCode = useCallback(async (code) => {
        if (!code) {
            setIsValid(true)
            setIsValidating(false)
            return
        }

        try {
            setIsValidating(true)
            await PromotionalCodeService.validatePromotionalCode(code)
            setIsValid(true)
        } catch (error) {
            console.error('Invalid voucher code:', error)
            setIsValid(false)
        } finally {
            setIsValidating(false)
        }
    }, [])

    useEffect(() => {
        validateCode(debouncedCode)
    }, [debouncedCode, validateCode])

    return {
        isValid,
        isValidating,
        debouncedCode,
    }
}
